import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

interface AdsContextValue {
  client: string | undefined;
}

const AdsContext = createContext<AdsContextValue | null>(null);

/** Injects the AdSense loader exactly once. Google's own CMP (Funding
 *  Choices) handles user consent, so the app no longer gates loading on a
 *  bespoke banner. */
function injectAdSense(client: string) {
  const id = 'adsbygoogle-loader';
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
  document.head.appendChild(s);
}

export function AdsProvider({ children }: { children: ReactNode }) {
  const client = import.meta.env.VITE_ADSENSE_CLIENT;

  useEffect(() => {
    if (client) injectAdSense(client);
  }, [client]);

  const value = useMemo<AdsContextValue>(() => ({ client }), [client]);

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
}

export function useAds(): AdsContextValue {
  const ctx = useContext(AdsContext);
  if (!ctx) throw new Error('useAds must be used within AdsProvider');
  return ctx;
}
